import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import Image from '@/components/Image';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { MLProject } from '@/types/resources';
import { api } from '@/utils/api';

const Thumbnail = styled(Image)`
  width: 90px;
  height: auto;
`;

const ThumbAndTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    &:last-child {
      cursor: initial;
    }
  }
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
  > * {
    font-weight: 700;
    font-size: 12px;
  }
`;

interface Props {
  project: MLProject;
}

const TableRow: FC<Props> = ({ project }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();
  const navigate = useNavigate();

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this project?`,
        project.title,
      );

      if (!confirm) return;

      await api.delete(`/v1/distribution/ml-projects/${project.id}`);

      await queryClient.invalidateQueries(['ml_projects']);

      alerts.success('Project was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onRowClick = () => {
    navigate(`../${project.id}`, { relative: 'path' });
  };

  return (
    <Tr onClick={onRowClick}>
      <Td>
        <ThumbAndTitle>
          <Thumbnail src={project.thumbnail?.versions.original} />
          <span>{project.title}</span>
        </ThumbAndTitle>
      </Td>
      <Td>{DateTime.fromISO(project.createdAt).toFormat('dd/LL/yyyy')}</Td>
      <Td>{DateTime.fromISO(project.updatedAt).toFormat('dd/LL/yyyy')}</Td>
      <Td>{project.totalLanguages}</Td>
      <Td>0</Td>
      <Td>0</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default TableRow;
